import { c as _c } from "react/compiler-runtime";
import { useStore } from 'zustand';
import { useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { type PianoKeyTone } from '@/components/piano/KeyboardPlate';
import { PianoKeyboard } from '@/components/piano/PianoKeyboard';
import { useKeyboard } from '@/hooks/keyboard/use-keyboard';
import { useStickyHeight } from '@/hooks/use-sticky-height';
import { useMeasuresStore, type MeasuresStore } from '@/hooks/store/use-measure-store';
import { useTranslation } from '@/hooks/use-translation';
import { octaveMarks } from '@/lib/piano/keyboard/keyboard-geometry';
import { cn } from '@/lib/utils';
import { isPassDone, passProgress, useMeasurePasses, type MeasurePassId } from './hooks/use-measure-passes';
export interface MeasureKeyboardPanelProps {
  currentKeyNumber: number | null;
  currentPassId: MeasurePassId;
  onKeySelect: (keyNumber: number) => void;
  onPassChange: (passId: MeasurePassId) => void;
}

/** Hoisted: a fresh object here would recompute the whole keyboard. */
const KEYBOARD_GEOMETRY = {
  keyHeight: 72
};

/**
 * Where the session stands: which pass is in hand, how far down the keyboard
 * it has got, and which key is being filled.
 *
 * It sticks to the top of the page, because the answer to "where am I?" is
 * needed exactly when the table has scrolled it out of sight — and it says
 * how tall it is, so that a field the focus moves to is scrolled into view
 * below it rather than behind it. On a phone, or any window under 600 px
 * tall, it would take a third to a half of the screen, before the on-screen
 * keyboard takes its share: there it scrolls away with the page instead.
 */
export const MeasureKeyboardPanel = t0 => {
  const $ = _c(95);
  const {
    currentKeyNumber,
    currentPassId,
    onKeySelect,
    onPassChange
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    keyboard
  } = useKeyboard();
  const panel = useRef(null);
  const [hoveredKey, setHoveredKey] = useState(null);
  useStickyHeight(panel, "--ptl-sticky-top");
  const measuredKeys = useStore(useMeasuresStore(), useShallow(_temp));
  let t1;
  if ($[0] !== keyboard) {
    t1 = keyboard.getKeys();
    $[0] = keyboard;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const keys = t1;
  const passes = useMeasurePasses();
  let t2;
  if ($[2] !== t) {
    t2 = t("Down & up weight");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("Front Weight", {
      ns: "navigation"
    });
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("Strike Weight", {
      ns: "navigation"
    });
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = {
      "down-up": t2,
      "front-weight": t3,
      "strike-weight": t4
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const passLabels = t5;
  let t6;
  if ($[12] !== keyboard.size || $[13] !== measuredKeys || $[14] !== passes) {
    t6 = passProgress(passes, measuredKeys, keyboard.size);
    $[12] = keyboard.size;
    $[13] = measuredKeys;
    $[14] = passes;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const progress = t6;
  let t7;
  if ($[16] !== currentKeyNumber || $[17] !== currentPassId || $[18] !== keys || $[19] !== measuredKeys || $[20] !== passes) {
    bb0: {
      let t8;
      if ($[22] !== currentPassId) {
        t8 = item => item.id === currentPassId;
        $[22] = currentPassId;
        $[23] = t8;
      } else {
        t8 = $[23];
      }
      const pass = passes.find(t8);
      const map = new Map();
      if (!pass) {
        t7 = map;
        break bb0;
      }
      keys.forEach(key => {
        const measured = measuredKeys[key.number - 1];
        if (!measured || !isPassDone(pass, measured)) {
          map.set(key.number, "pending");
        }
      });
      if (currentKeyNumber !== null) {
        map.set(currentKeyNumber, "current");
      }
      t7 = map;
    }
    $[16] = currentKeyNumber;
    $[17] = currentPassId;
    $[18] = keys;
    $[19] = measuredKeys;
    $[20] = passes;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const tones = t7;
  let t8;
  if ($[24] !== currentKeyNumber || $[25] !== keyboard) {
    t8 = currentKeyNumber === null ? null : keyboard.getKeyByNumber(currentKeyNumber);
    $[24] = currentKeyNumber;
    $[25] = keyboard;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const currentKey = t8;
  let t9;
  if ($[27] !== keys) {
    t9 = octaveMarks(keys).map(_temp2);
    $[27] = keys;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const labels = t9;
  let t10;
  if ($[29] !== t) {
    t10 = t("Keyboard and measuring pass");
    $[29] = t;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t) {
    t11 = t("Measuring pass");
    $[31] = t;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== currentPassId || $[34] !== keyboard.size || $[35] !== onPassChange || $[36] !== passLabels || $[37] !== passes || $[38] !== progress) {
    let t13;
    if ($[40] !== currentPassId || $[41] !== keyboard.size || $[42] !== onPassChange || $[43] !== passLabels || $[44] !== progress) {
      t13 = (pass_0, index) => {
        const isSelected = pass_0.id === currentPassId;
        return <button key={pass_0.id} type="button" aria-pressed={isSelected} onClick={() => onPassChange(pass_0.id)} className={cn("inline-flex h-9 cursor-pointer items-center gap-2 px-3 text-[13px] font-medium transition-colors", index > 0 && "border-l border-input", isSelected ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:bg-accent")}><span className="font-mono font-semibold">{pass_0.number}</span>{passLabels[pass_0.id]}<span className="font-mono text-[11px] opacity-80">{progress.get(pass_0.id) ?? 0}/{keyboard.size}</span></button>;
      };
      $[40] = currentPassId;
      $[41] = keyboard.size;
      $[42] = onPassChange;
      $[43] = passLabels;
      $[44] = progress;
      $[45] = t13;
    } else {
      t13 = $[45];
    }
    t12 = passes.map(t13);
    $[33] = currentPassId;
    $[34] = keyboard.size;
    $[35] = onPassChange;
    $[36] = passLabels;
    $[37] = passes;
    $[38] = progress;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[46] !== t11 || $[47] !== t12) {
    t13 = <div role="group" aria-label={t11} className="inline-flex flex-wrap overflow-hidden rounded-md border border-ptl-rule-strong">{t12}</div>;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== currentKey || $[50] !== t) {
    t14 = currentKey ? <div className="flex items-baseline gap-2.5"><span className="ptl-label">{t("Current key")}</span><span className="font-serif text-[28px] leading-none">{currentKey.name}</span><span className="font-mono text-[13px] font-semibold text-ptl-current">#{currentKey.number}</span></div> : null;
    $[49] = currentKey;
    $[50] = t;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== t13 || $[53] !== t14) {
    t15 = <div className="flex flex-wrap items-center justify-between gap-3">{t13}{t14}</div>;
    $[52] = t13;
    $[53] = t14;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== t) {
    t16 = t("Keyboard: click a key to jump to it");
    $[55] = t;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  const t17 = hoveredKey === currentKeyNumber ? null : hoveredKey;
  let t18;
  if ($[57] !== keys || $[58] !== labels || $[59] !== onKeySelect || $[60] !== t16 || $[61] !== t17 || $[62] !== tones) {
    t18 = <PianoKeyboard keys={keys} description={t16} geometry={KEYBOARD_GEOMETRY} highlightedKey={t17} labels={labels} onKeyHover={setHoveredKey} onKeySelect={onKeySelect} toneByKeyNumber={tones} />;
    $[57] = keys;
    $[58] = labels;
    $[59] = onKeySelect;
    $[60] = t16;
    $[61] = t17;
    $[62] = tones;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  let t19;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span className="size-3 rounded-sm bg-ptl-key-white shadow-[inset_0_0_0_1px_var(--input)]" />;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  let t20;
  if ($[65] !== t) {
    t20 = t("Measured in this pass");
    $[65] = t;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== t20) {
    t21 = <span className="inline-flex items-center gap-[7px]">{t19}{t20}</span>;
    $[67] = t20;
    $[68] = t21;
  } else {
    t21 = $[68];
  }
  let t22;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span className="size-3 rounded-sm bg-ptl-current" />;
    $[69] = t22;
  } else {
    t22 = $[69];
  }
  let t23;
  if ($[70] !== t) {
    t23 = t("Current");
    $[70] = t;
    $[71] = t23;
  } else {
    t23 = $[71];
  }
  let t24;
  if ($[72] !== t23) {
    t24 = <span className="inline-flex items-center gap-[7px]">{t22}{t23}</span>;
    $[72] = t23;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  let t25;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span className="size-3 rounded-sm bg-ptl-key-white-pending shadow-[inset_0_0_0_1px_var(--input)]" />;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  let t26;
  if ($[75] !== t) {
    t26 = t("Still to measure");
    $[75] = t;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  let t27;
  if ($[77] !== t26) {
    t27 = <span className="inline-flex items-center gap-[7px]">{t25}{t26}</span>;
    $[77] = t26;
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  let t28;
  if ($[79] !== t21 || $[80] !== t24 || $[81] !== t27) {
    t28 = <div className="flex flex-wrap gap-4">{t21}{t24}{t27}</div>;
    $[79] = t21;
    $[80] = t24;
    $[81] = t27;
    $[82] = t28;
  } else {
    t28 = $[82];
  }
  let t29;
  if ($[83] !== t) {
    t29 = t("Click a key to jump to it");
    $[83] = t;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  let t30;
  if ($[85] !== t29) {
    t30 = <span className="text-ptl-ink-3">{t29}</span>;
    $[85] = t29;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  let t31;
  if ($[87] !== t28 || $[88] !== t30) {
    t31 = <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2 text-[12.5px] text-muted-foreground">{t28}{t30}</div>;
    $[87] = t28;
    $[88] = t30;
    $[89] = t31;
  } else {
    t31 = $[89];
  }
  let t32;
  if ($[90] !== t10 || $[91] !== t15 || $[92] !== t18 || $[93] !== t31) {
    t32 = <section ref={panel} aria-label={t10} className="sticky top-0 z-10 flex flex-col gap-3 rounded-md border border-ptl-rule-strong bg-card px-4 pt-3.5 pb-3 shadow-[0_14px_28px_-18px_rgb(0_0_0/0.45)] max-md:static [@media(max-height:600px)]:static">{t15}{t18}{t31}</section>;
    $[90] = t10;
    $[91] = t15;
    $[92] = t18;
    $[93] = t31;
    $[94] = t32;
  } else {
    t32 = $[94];
  }
  return t32;
};
function _temp(state) {
  return state.keys;
}
function _temp2(mark) {
  return {
    at: mark.number,
    isMuted: true,
    text: mark.label
  };
}